import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import Select from "react-select";
import { useAuthToken } from "../TokenContext";
import { useIntl } from "react-intl";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import moment from "moment";
import {
  Drawer,
  TextField,
  Button,
  CircularProgress,
  alpha,
  useTheme,
  styled,
  Paper,
  Typography,
  Box,
  Checkbox,
  Fab,
  Slide,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useSelector } from "react-redux";
import "./kanband.css";
import { Snackbar, Alert } from '@mui/material';

import { FaBug, FaJira } from 'react-icons/fa';

// Styled Components
const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ theme, collapsed }) => ({
  position: 'relative',
  width: '100%',
  marginLeft: 0,
  overflowX: 'hidden',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    width: '100%'
  },
  backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : '#F5F5F5',
  color: theme.palette.text.primary,
  minHeight: 'calc(100vh - 64px)',
  padding: theme.spacing(3)
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
    color: theme.palette.text.primary,
  }
}));

const FiltersContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    '& > *': {
      width: '100%'
    }
  }
}));

const SelectWrapper = styled(Box)(({ theme }) => ({
  width: "20rem",
  marginBottom: theme.spacing(4),
  '& .select__control': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    borderColor: theme.palette.divider,
    minHeight: 56,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    }
  },
  '& .select__menu': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    color: theme.palette.text.primary,
    zIndex: 999,
    boxShadow: theme.shadows[3]
  },
  '& .select__option': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    color: theme.palette.text.primary,
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1)
    }
  }
}));

const SprintSelect = styled('select')(({ theme }) => ({
  textDecoration: 'none',
  border: '1px solid rgb(233, 224, 224)',
  borderRadius: '4px',
  width: "20rem",
  height: '56px',
  padding: '0 0.5rem',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  color: theme.palette.text.primary,
  cursor: 'pointer',
  '&:focus': {
    borderColor: theme.palette.primary.main,
    outline: 'none',
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  }
}));

const StyledColumn = styled(Box)(({ theme, collapsed }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  minWidth: '280px',
  width: '280px',
  marginRight: theme.spacing(2),
  maxHeight: 'calc(100vh - 220px)',
  overflowY: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  boxShadow: theme.shadows[1],
  '& .column-header': {
    borderBottom: '3px solid',
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }
}));

const StyledTask = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: `6px solid ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2]
  },
  '& .task-content': {
    width: '100%',
    textAlign: 'left'
  },
  '& .task-header': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  '& .task-title': {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary
  },
  '& .task-id': {
    fontSize: '0.75rem',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '2px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    marginBottom: theme.spacing(1)
  },
  '& .task-assignee': {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  '& .task-due-date': {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  }
}));

const ScrollButton = styled(Button)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 40,
  height: 40,
  minWidth: 40,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '50%',
  boxShadow: theme.shadows[2],
  zIndex: 1,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const ActionBar = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  borderTop: `2px solid ${theme.palette.primary.main}`,
  boxShadow: theme.shadows[8],
  zIndex: 1100,
}));

const KanbanBoard = ({ collapsed }) => {
  // State declarations
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState({
    "To Do": { title: "To Do", items: [], type: "open" },
    "In Progress": { title: "In Progress", items: [], type: "open" },
    Done: { title: "Done", items: [], type: "closed" },
  });
  const [selectedPodId, setSelectedPodId] = useState([]);
  const [authorizationDetails, setAuthorizationDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState([]);
  const [podNames, setPodNames] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isAssigningAgent, setIsAssigningAgent] = useState(false);
  const theme = useTheme();
  const [sprintData, setSprintData] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState({
    name: "",
    startDate: "",
    endDate: "",
  });
  const token = useAuthToken();
  const intl = useIntl();
  const { user } = useSelector((state) => state.auth);
  const scrollContainerRef = useRef(null);

  // Flow management states
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [selectedAgentForBulk, setSelectedAgentForBulk] = useState(null);
  const [showAgentSelectionDialog, setShowAgentSelectionDialog] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // ============================================================================
  // COMPLETE FLOW: AGENT ASSIGNMENT → CODE GENERATION → NOTIFICATION
  // ============================================================================

  const simulateAgentWork = async (taskId, taskSummary, agentId, agentName) => {
    // Simulate agent working (5-10 seconds)
    const workDuration = Math.floor(Math.random() * 5000) + 5000;

    console.log(`Agent ${agentName} started working on task ${taskId}`);

    // Show initial notification
    setNotificationMessage(`🤖 ${agentName} has started working on: ${taskSummary}`);
    setShowNotification(true);

    // Wait for work duration
    await new Promise(resolve => setTimeout(resolve, workDuration));

    // Create mock approval
    const mockApproval = {
      id: `approval_${Date.now()}`,
      manager_id: user.data[0].emp_id,
      agent_action: 'code_implementation',
      task_type: 'code_implementation',
      task_summary: taskSummary,
      task_id: taskId,
      agent_id: agentId,
      agent_name: agentName,
      entity_data: JSON.stringify({
        task_id: taskId,
        agent_id: agentId,
        agent_name: agentName,
        work_completed: true,
        files_modified: [
          'src/components/Feature.jsx',
          'src/utils/helper.js',
          'src/api/service.js'
        ],
        lines_added: 145,
        lines_deleted: 32,
        tests_added: 8,
        completion_time: new Date().toISOString(),
        description: `Completed coding task: ${taskSummary}\n\nChanges made:\n- Implemented main feature logic\n- Added unit tests (8 tests, 100% coverage)\n- Updated documentation\n- Fixed edge cases\n\nReady for review and approval.`
      }),
      status: 'pending',
      created_at: new Date().toISOString(),
      jira_output: `Task ${taskId} implementation completed by ${agentName}.\n\nSummary:\n✅ Feature implementation complete\n✅ Code review passed\n✅ Tests: 8 new tests (100% coverage)\n✅ Documentation updated\n\nChanges:\n+ 145 lines added\n- 32 lines removed\n\nFiles modified:\n1. src/components/Feature.jsx\n2. src/utils/helper.js\n3. src/api/service.js\n\nAll checks passed. Ready for manager approval.`
    };

    // Store in localStorage
    const existingApprovals = JSON.parse(localStorage.getItem('mock_approvals') || '[]');
    existingApprovals.push(mockApproval);
    localStorage.setItem('mock_approvals', JSON.stringify(existingApprovals));

    // Show completion notification
    setNotificationMessage(`✅ ${agentName} completed work on: ${taskSummary}. Approval required!`);
    setShowNotification(true);

    console.log(`Agent ${agentName} completed work on task ${taskId}. Approval pending.`);
  };

  const handleCompleteFlow = async () => {
    if (selectedTasks.length === 0) {
      alert('Please select at least one task');
      return;
    }

    // Step 1: Show agent selection dialog
    setShowAgentSelectionDialog(true);
  };

  const handleStartWorkflow = async () => {
    if (!selectedAgentForBulk) {
      alert('Please select an agent');
      return;
    }

    setShowAgentSelectionDialog(false);
    setIsGeneratingCode(true);
    setCurrentProgress(0);

    const selectedTaskDetails = tasks.filter(task =>
      selectedTasks.includes(task.key || task.pod_task_id)
    );

    const agent = agents.find(a => a.id === selectedAgentForBulk);
    const totalTasks = selectedTaskDetails.length;

    try {
      // Step 2: Assign agent to all selected tasks
      setProgressMessage('Step 1/3: Assigning agent to tasks...');
      for (let i = 0; i < selectedTaskDetails.length; i++) {
        const task = selectedTaskDetails[i];
        await assignAgentToTask(task, selectedAgentForBulk, agent);
        setCurrentProgress(((i + 1) / totalTasks) * 33);
        await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for UI feedback
      }

      // Step 3: Generate code for all tasks
      setProgressMessage('Step 2/3: Generating code...');
      setCurrentProgress(33);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate code generation
      setCurrentProgress(66);

      setNotificationMessage(`🤖 Agent "${agent.name}" is working on ${totalTasks} ${totalTasks === 1 ? 'task' : 'tasks'}...`);
      setShowNotification(true);

      // Step 4: Simulate agent work and create approvals
      setProgressMessage('Step 3/3: Agent working on tasks...');
      for (let i = 0; i < selectedTaskDetails.length; i++) {
        const task = selectedTaskDetails[i];
        await simulateAgentWork(
          task.key || task.pod_task_id,
          task.summary || task.pod_task_name,
          selectedAgentForBulk,
          agent.name
        );
        setCurrentProgress(66 + ((i + 1) / totalTasks) * 34);
      }

      // Success - clear selection
      setProgressMessage('Complete!');
      setCurrentProgress(100);

      setTimeout(() => {
        setIsGeneratingCode(false);
        setSelectedTasks([]);
        setSelectedAgentForBulk(null);
        setNotificationMessage(`✅ All ${totalTasks} tasks completed! Check Pending Approvals.`);
        setShowNotification(true);
      }, 1000);

    } catch (error) {
      console.error('Error in workflow:', error);
      setNotificationMessage('❌ Error during workflow execution');
      setShowNotification(true);
      setIsGeneratingCode(false);
    }
  };

  const assignAgentToTask = async (task, agentId, agent) => {
    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      // Update local state immediately
      setTasks(prevTasks =>
        prevTasks.map(t =>
          (t.key || t.pod_task_id) === (task.key || task.pod_task_id)
            ? { ...t, assigned_agent: agent }
            : t
        )
      );

      // Update columns
      setColumns(prevColumns => {
        const newColumns = { ...prevColumns };
        Object.keys(newColumns).forEach(columnKey => {
          newColumns[columnKey].items = newColumns[columnKey].items.map(t =>
            (t.key || t.pod_task_id) === (task.key || task.pod_task_id)
              ? { ...t, assigned_agent: agent }
              : t
          );
        });
        return newColumns;
      });

      // Make API call (if you have the endpoint)
      if (process.env.REACT_APP_CLIENT_ASSIGNAGENTTOTASK) {
        await axios.post(
          process.env.REACT_APP_CLIENT_ASSIGNAGENTTOTASK,
          {
            task_id: task.key || task.pod_task_id,
            agent_id: agentId,
            task_type: task.key ? 'jira' : 'pod'
          },
          { headers }
        );
      }
    } catch (error) {
      console.error("Error assigning agent:", error);
    }
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') return;
    setShowNotification(false);
  };

  const handleCloseAgentDialog = () => {
    setShowAgentSelectionDialog(false);
    setSelectedAgentForBulk(null);
  };

  // Initial data fetching
  useEffect(() => {
    fetchPodNames();
    fetchAgents();
  }, [token]);

  useEffect(() => {
    if (selectedPodId.length > 0) {
      fetchAuthorizationTokensAndList();
    } else {
      resetStates();
    }
  }, [selectedPodId, selectedCandidate, selectedSprint.name]);

  const resetStates = () => {
    setCandidates([]);
    setTasks([]);
    setSelectedTasks([]);
    setColumns({
      "To Do": { title: "To Do", items: [], type: "open" },
      "In Progress": { title: "In Progress", items: [], type: "open" },
      Done: { title: "Done", items: [], type: "closed" },
    });
  };

  // Helper functions
  const parseStatusString = (statusString) => {
    if (!statusString) return [];
    return statusString.split(",").map((status) => status.trim());
  };

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      scrollContainerRef.current.scrollTo({
        left: currentScroll + (direction === "left" ? -scrollAmount : scrollAmount),
        behavior: "smooth",
      });
    }
  };

  // Task selection handlers
  const handleTaskSelection = (taskId, event) => {
    event.stopPropagation();
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };

  const handleClearSelection = () => {
    setSelectedTasks([]);
  };

  // Agent fetching function
  const fetchAgents = async () => {
    if (!user?.data?.[0]?.client_code) return;

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      const response = await axios.post(
        "https://5weygwj9fd.execute-api.us-east-1.amazonaws.com/prod/listAgentsHandler",
        {
          env: "prod",
          org_id: user.data[0].client_code,
          limit: 100,
          offset: 0
        },
        { headers }
      );

      if (response.data.status === 200 && response.data.data.agents) {
        setAgents(response.data.data.agents);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  // Agent assignment handler (for single task in drawer)
  const handleAssignAgent = async (agentId) => {
    if (!selectedTask || isAssigningAgent) return;

    setIsAssigningAgent(true);

    try {
      const headers = {
        Authorization: token,
        "Content-Type": "application/json",
      };

      const agent = agents.find(a => a.id === agentId);

      if (process.env.REACT_APP_CLIENT_ASSIGNAGENTTOTASK) {
        const response = await axios.post(
          process.env.REACT_APP_CLIENT_ASSIGNAGENTTOTASK,
          {
            task_id: selectedTask.key || selectedTask.pod_task_id,
            agent_id: agentId,
            task_type: selectedTask.key ? 'jira' : 'pod'
          },
          { headers }
        );

        if (response.status === 200) {
          // Update local state
          setSelectedTask(prev => ({
            ...prev,
            assigned_agent: agent
          }));

          // Update tasks array
          setTasks(prevTasks =>
            prevTasks.map(task =>
              (task.key || task.pod_task_id) === (selectedTask.key || selectedTask.pod_task_id)
                ? { ...task, assigned_agent: agent }
                : task
            )
          );

          // Update columns
          setColumns(prevColumns => {
            const newColumns = { ...prevColumns };
            Object.keys(newColumns).forEach(columnKey => {
              newColumns[columnKey].items = newColumns[columnKey].items.map(task =>
                (task.key || task.pod_task_id) === (selectedTask.key || selectedTask.pod_task_id)
                  ? { ...task, assigned_agent: agent }
                  : task
              );
            });
            return newColumns;
          });

          setNotificationMessage(`Agent "${agent.name}" assigned successfully!`);
          setShowNotification(true);

          // Start agent work simulation
          simulateAgentWork(
            selectedTask.key || selectedTask.pod_task_id,
            selectedTask.summary || selectedTask.pod_task_name,
            agentId,
            agent.name
          );
        }
      }
    } catch (error) {
      console.error("Error assigning agent:", error);
      alert("Failed to assign agent. Please ensure the backend endpoint is configured.");
    } finally {
      setIsAssigningAgent(false);
    }
  };

  // Remove agent assignment
  const handleRemoveAgent = async () => {
    if (!selectedTask || !selectedTask.assigned_agent || isAssigningAgent) return;

    setIsAssigningAgent(true);

    try {
      const headers = {
        Authorization: token,
        "Content-Type": "application/json",
      };

      if (process.env.REACT_APP_CLIENT_REMOVEAGENTFROMTASK) {
        const response = await axios.post(
          process.env.REACT_APP_CLIENT_REMOVEAGENTFROMTASK,
          {
            task_id: selectedTask.key || selectedTask.pod_task_id,
            task_type: selectedTask.key ? 'jira' : 'pod'
          },
          { headers }
        );

        if (response.status === 200) {
          // Update local state
          setSelectedTask(prev => ({
            ...prev,
            assigned_agent: null
          }));

          // Update tasks array
          setTasks(prevTasks =>
            prevTasks.map(task =>
              (task.key || task.pod_task_id) === (selectedTask.key || selectedTask.pod_task_id)
                ? { ...task, assigned_agent: null }
                : task
            )
          );

          // Update columns
          setColumns(prevColumns => {
            const newColumns = { ...prevColumns };
            Object.keys(newColumns).forEach(columnKey => {
              newColumns[columnKey].items = newColumns[columnKey].items.map(task =>
                (task.key || task.pod_task_id) === (selectedTask.key || selectedTask.pod_task_id)
                  ? { ...task, assigned_agent: null }
                  : task
              );
            });
            return newColumns;
          });

          alert("Agent removed successfully!");
        }
      }
    } catch (error) {
      console.error("Error removing agent:", error);
      alert("Failed to remove agent. Please ensure the backend endpoint is configured.");
    } finally {
      setIsAssigningAgent(false);
    }
  };

  // Get agent status color
  const getAgentStatusColor = (status) => {
    switch (status) {
      case 'provisioning':
        return 'warning';
      case 'running':
      case 'active':
        return 'success';
      case 'stopped':
        return 'error';
      default:
        return 'default';
    }
  };

  // Column initialization
  const initializeColumns = (authData) => {
    if (!authData) return columns;

    const openStatuses = parseStatusString(authData.jira_open_story_bug_status?.[0]);
    const closedStatuses = parseStatusString(authData.jira_closed_story_bug_status?.[0]);

    const newColumns = {};
    openStatuses.forEach((status) => {
      newColumns[status] = { title: status, items: [], type: "open" };
    });
    closedStatuses.forEach((status) => {
      newColumns[status] = { title: status, items: [], type: "closed" };
    });

    return newColumns;
  };

  // Task grouping
  const groupTasksByStatus = (tasks, columns) => {
    const groupedColumns = { ...columns };
    Object.keys(groupedColumns).forEach((key) => {
      groupedColumns[key].items = [];
    });

    tasks.forEach((task) => {
      const status = task.status || "To Do";
      if (groupedColumns[status]) {
        groupedColumns[status].items.push(task);
      }
    });

    return groupedColumns;
  };

  // Comment rendering and handling functions
  const renderJiraComment = (content) => {
    if (!content) return null;

    return content.map((item, index) => {
      if (item.type === 'paragraph') {
        return (
          <div key={index} style={{ marginBottom: '8px' }}>
            {item.content?.map((contentItem, cIndex) => {
              if (contentItem.type === 'text') {
                return <span key={cIndex}>{contentItem.text}</span>;
              }
              if (contentItem.type === 'mention') {
                return (
                  <span
                    key={cIndex}
                    style={{
                      color: '#0052CC',
                      fontWeight: '500',
                      backgroundColor: '#EAE6FF',
                      padding: '0 4px',
                      borderRadius: '3px',
                      margin: '0 2px'
                    }}
                  >
                    {contentItem.attrs.text}
                  </span>
                );
              }
              return contentItem.type === 'hardBreak' ? <br key={cIndex} /> : null;
            })}
          </div>
        );
      }
      return null;
    });
  };

  const renderComments = () => {
    if (!selectedTask) return null;

    if (selectedTask.pod_task_id && selectedTask.comment) {
      try {
        const podComments = JSON.parse(selectedTask.comment);
        return podComments.map((comment, index) => (
          <div
            key={index}
            className="comment"
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : '#f8f9fa',
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "12px",
              alignItems: "center",
            }}>
              <span style={{
                backgroundColor: "#0073e6",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "500",
              }}>
                {comment.author}
              </span>
              <small style={{ color: "#666" }}>
                {moment(comment.timestamp).format("MMM DD, YYYY HH:mm")}
              </small>
            </div>
            <div style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: "1.5",
            }}>
              {comment.text}
            </div>
          </div>
        ));
      } catch (error) {
        console.error("Error parsing POD comments:", error);
        return null;
      }
    }

    return (
      <>
        <Box><Typography variant="subtitle1">Comments</Typography></Box>
        {selectedTask.comments?.map((comment, index) => (
          <div
            key={index}
            className="comment"
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : '#f8f9fa',
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "12px",
              alignItems: "center",
            }}>
              <span style={{
                backgroundColor: "#0073e6",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "500",
              }}>
                {comment.author}
              </span>
              <small style={{ color: "#666" }}>
                {moment(comment.created).format("MMM DD, YYYY HH:mm")}
              </small>
            </div>
            <div style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: "1.5",
            }}>
              {renderJiraComment(comment.body?.content)}
            </div>
          </div>
        ))}
      </>
    );
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      if (selectedTask.pod_task_id) {
        let existingComments = [];
        if (selectedTask.comment) {
          try {
            existingComments = JSON.parse(selectedTask.comment);
          } catch (error) {
            console.error("Error parsing existing comments:", error);
            existingComments = [];
          }
        }

        const newCommentObj = {
          pod_task_id: parseInt(selectedTask.pod_task_id),
          user_id: parseInt(user.data[0].emp_id),
          new_text: newComment,
          is_reply: false,
          author: user.data[0].emp_firstname
        };

        const response = await axios.post(
          process.env.REACT_APP_CLIENT_ADDCOMENTPODTASK,
          [newCommentObj],
          { headers }
        );

        if (response.status === 200) {
          setSelectedTask(prevTask => ({
            ...prevTask,
            comment: JSON.stringify([
              ...existingComments,
              {
                text: newComment,
                user_id: parseInt(user.data[0].emp_id),
                timestamp: new Date().toISOString(),
                replies: []
              }
            ])
          }));
          setNewComment("");
        }
      } else if (selectedTask.key) {
        const response = await axios.post(
          "https://o0quulyyma.execute-api.us-east-1.amazonaws.com/prod/addCommentjirapodboard",
          {
            jiraUrl: authorizationDetails.Jira_URL,
            issueKey: selectedTask.key,
            commentText: newComment,
            username: authorizationDetails.Jira_admin_login_email_address,
            password: authorizationDetails.Jira_admin_API_Token,
          },
          { headers }
        );

        if (response.status === 200) {
          setSelectedTask((prevTask) => ({
            ...prevTask,
            comments: [
              ...(prevTask.comments || []),
              {
                author: user.data[0].emp_name || "current_user",
                body: {
                  type: "doc",
                  version: 1,
                  content: [
                    {
                      type: "paragraph",
                      content: [{ type: "text", text: newComment }],
                    },
                  ],
                },
                created: new Date().toISOString(),
              },
            ],
          }));
          setNewComment("");
        }
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };

  // Data fetching functions
  const fetchPodNames = async () => {
    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };
    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_FETCHPODSFORMANAGER,
        { clientID: user.data[0].emp_id },
        { headers }
      );

      const data = response.data.data;
      const podNameOptions = data.map((pod) => ({
        label: pod.podName,
        value: pod.id,
      }));

      setPodNames(podNameOptions);
    } catch (error) {
      console.error("Error fetching pod names:", error);
    }
  };

  useEffect(() => {
    if (user.data[0].type === "stakeholder") {
      if (!token) {
        console.log("Token is missing. API requests will not be made.");
        return;
      }
      fetch(process.env.REACT_APP_STAKEHOLDER_SPRINTFILTERSTAKEHOLDER, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          manager_id: user.data[0].emp_id,
          org_code: user.data[0].client_code,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.statusCode === 200 && data.body) {
            const parsedBody = JSON.parse(data.body);
            if (Array.isArray(parsedBody.sprint_filter)) {
              setSprintData(parsedBody.sprint_filter);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching data from stakeholder API:", error);
        });
    } else {
      if (!token) {
        console.log("Token is missing. API requests will not be made.");
        return;
      }
      fetch(process.env.REACT_APP_CLIENT_SPRINTFILTERMANAGER, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          manager_id: user.data[0].emp_id,
          org_code: user.data[0].client_code,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.statusCode === 200 && data.body) {
            const parsedBody = JSON.parse(data.body);
            if (Array.isArray(parsedBody.sprint_filter)) {
              setSprintData(parsedBody.sprint_filter);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    }
  }, [token]);

  const fetchAuthorizationTokensAndList = async () => {
    setLoading(true);

    if (!selectedPodId || selectedPodId.length === 0) {
      setLoading(false);
      return;
    }

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      const authorizationResponse = await axios.post(
        process.env.REACT_APP_CUSTOMERADMIN_FETCHAUTHORIZATIONTOKENS,
        { Organizational_Code: user.data[0].client_code },
        { headers }
      );

      const authorizationData = authorizationResponse.data.body.data[0];
      setAuthorizationDetails(authorizationData);

      const initialColumns = initializeColumns(authorizationData);
      setColumns(initialColumns);

      if (selectedPodId.length > 0) {
        const emailFetchingResponse = await axios.post(
          process.env.REACT_APP_CLIENT_FETCHPODCANDIDATES,
          { podID: selectedPodId },
          { headers }
        );

        const allCandidatesData = emailFetchingResponse?.data?.data;
        const allCandidates = Array.isArray(allCandidatesData) ? allCandidatesData : [];
        setCandidates(allCandidates);

        const candidateNames = selectedCandidate.map(option => option.value);

        if (selectedSprint.name) {
          const listPayload = {
            pod_Id: selectedPodId,
            username: authorizationData.Jira_admin_login_email_address,
            password: authorizationData.Jira_admin_API_Token,
            projectKey: authorizationData.Jira_Kanben_Project_name,
            jira_url: authorizationData.Jira_URL,
            assignees: Array.isArray(allCandidates) ? allCandidates.map(candidate => candidate.email) : [],
            candidateName: candidateNames,
            sprintName: selectedSprint.name
          };

          const response = await axios.post(
            "https://irl3zjh84e.execute-api.us-east-1.amazonaws.com/prod/testSingleviewtask",
            listPayload,
            { headers }
          );

          const combinedTasks = [
            ...(response.data.data.podMembers || []).map((task) => ({
              ...task,
              comments: task.comments || [],
            })),
            ...(response.data.data.jiraIssues || []).map((task) => ({
              ...task,
              comments: task.comments || [],
            })),
          ];

          setTasks(combinedTasks);
          setColumns(groupTasksByStatus(combinedTasks, initialColumns));
        } else {
          setTasks([]);
        }
      }
    } catch (error) {
      console.error("Error in fetchAuthorizationTokensAndList:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = async (task) => {
    const fullTask = tasks.find(
      (t) => (t.key || t.pod_task_id) === (task.key || task.pod_task_id)
    );

    if (fullTask) {
      setSelectedTask(fullTask);
      if (fullTask.pod_task_id) {
        try {
          const headers = {
            Authorization: token,
            "Content-Type": "application/json",
          };

          const response = await axios.post(
            "https://6w88fzkr0j.execute-api.us-east-1.amazonaws.com/prod/fetchpodtaskComment",
            { pod_task_id: parseInt(fullTask.pod_task_id) },
            { headers }
          );
        } catch (error) {
          console.error("Error fetching pod task comments:", error);
        }
      }
    } else {
      setSelectedTask({
        ...task,
        comments: [],
      });
    }
  };

  const handleDrawerClose = () => {
    setSelectedTask(null);
    setNewComment("");
    setSelectedAgent(null);
  };

  // Drag and drop handlers
  const onDragEnd = async (result) => {
    if (!result.destination || isUpdating) return;

    const { source, destination, draggableId } = result;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const task = Object.values(columns).reduce((found, column) => {
      if (found) return found;
      return column.items.find(
        (task) => (task.key || task.pod_task_id).toString() === draggableId
      );
    }, null);

    if (!task) return;

    const oldColumns = columns;
    const newColumns = { ...columns };
    const sourceColumn = columns[source.droppableId];
    const destColumn = columns[destination.droppableId];

    const sourceItems = [...sourceColumn.items];
    const destItems = [...destColumn.items];

    const [removed] = sourceItems.splice(source.index, 1);
    const movedTask = { ...removed, status: destination.droppableId };
    destItems.splice(destination.index, 0, movedTask);

    newColumns[source.droppableId] = {
      ...sourceColumn,
      items: sourceItems,
    };
    newColumns[destination.droppableId] = {
      ...destColumn,
      items: destItems,
    };

    setColumns(newColumns);
    setIsUpdating(true);

    try {
      const headers = {
        Authorization: token,
        "Content-Type": "application/json",
      };

      if (!authorizationDetails?.Jira_admin_API_Token ||
        !authorizationDetails?.Jira_admin_login_email_address ||
        !authorizationDetails?.Jira_URL) {
        throw new Error("Missing authorization details");
      }

      if (task.pod_task_id) {
        const updateResponse = await axios.put(
          process.env.REACT_APP_CLIENT_UPDATEKANBANBOARDPODTASK,
          {
            pod_task_id: task.pod_task_id,
            status: destination.droppableId,
          },
          { headers }
        );

        if (updateResponse.status !== 200) {
          throw new Error("Failed to update pod task status");
        }
      } else {
        const transitionId = await fetchTransitionId(
          destination.droppableId,
          task.key
        );

        if (!transitionId) {
          throw new Error("Invalid transition for this status change");
        }

        const updateResponse = await axios.put(
          process.env.REACT_APP_CLIENT_KANBANBOARDUPDATE,
          {
            Jira_URL: authorizationDetails.Jira_URL,
            Jira_admin_login_email_address: authorizationDetails.Jira_admin_login_email_address,
            Jira_admin_API_Token: authorizationDetails.Jira_admin_API_Token,
            key: task.key,
            transitionId: transitionId,
            status: destination.droppableId,
          },
          {
            headers,
            timeout: 10000
          }
        );

        if (updateResponse.status !== 200) {
          throw new Error("Failed to update JIRA task status");
        }
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      setColumns(oldColumns);
      alert("Failed to update task status. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchTransitionId = async (status, key) => {
    if (!status || !key) return null;

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_FETCHSTATUSFORSPECIFICJIRAISSUE,
        {
          Jira_admin_login_email_address: authorizationDetails.Jira_admin_login_email_address,
          Jira_admin_API_Token: authorizationDetails.Jira_admin_API_Token,
          key: key,
          Jira_URL: authorizationDetails.Jira_URL,
          status: status,
        },
        {
          headers,
          timeout: 5000
        }
      );

      if (!response.data?.body) {
        throw new Error("Invalid response format");
      }

      const statusData = response.data.body;
      const matchingStatus = statusData.find((s) => s.name === status);

      return matchingStatus?.id || null;
    } catch (error) {
      console.error("Error fetching transitionId:", error);
      return null;
    }
  };

  // Return/Render section
  // Return/Render section
  return (
    <div>
      <Box sx={{ p: 4 }}>
        <Typography className="artemy-page-title" gutterBottom>
          {intl.formatMessage({ id: "podBoard" })}
        </Typography>
        <Typography className="artemy-page-sub" gutterBottom>
          Manage and track your pod tasks and AI agent work in real-time.
        </Typography>

        <Box className="artemy-card" sx={{ p: 3, mb: 4, bgcolor: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)' }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ width: '300px' }}>
              <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', mb: 1, display: 'block' }}>
                {intl.formatMessage({ id: "podSelection" })}
              </Typography>
              <Select
                isMulti
                menuPortalTarget={document.body}
                menuPosition="fixed"
                options={podNames}
                value={podNames.filter((option) => selectedPodId.includes(option.value))}
                onChange={(selectedOptions) => setSelectedPodId(selectedOptions.map((opt) => opt.value))}
                placeholder={intl.formatMessage({ id: "selectPodsRequired" })}
                className="artemy-select-container"
                classNamePrefix="artemy-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    background: 'var(--artemy-bg-elevated)',
                    borderColor: 'var(--artemy-border)',
                    '&:hover': { borderColor: 'var(--artemy-primary)' }
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({
                    ...base,
                    background: 'var(--artemy-bg-elevated)',
                    border: '1px solid var(--artemy-border)',
                    zIndex: 9999
                  }),
                  option: (base, state) => ({
                    ...base,
                    background: state.isFocused ? 'rgba(255,255,255,0.05)' : 'transparent',
                    color: 'var(--artemy-text-primary)'
                  }),
                  multiValue: (base) => ({
                    ...base,
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '4px'
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: 'var(--artemy-text-primary)'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: 'var(--artemy-text-secondary)'
                  })
                }}
              />
            </Box>

            {selectedPodId?.length > 0 && (
              <>
                <Box sx={{ width: '300px' }}>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', mb: 1, display: 'block' }}>
                    Sprint Selection *
                  </Typography>
                  <select
                    className="artemy-input"
                    style={{ height: '38px', paddingTop: '4px', paddingBottom: '4px' }}
                    value={selectedSprint.name}
                    onChange={(e) => {
                      const selectedSprintName = e.target.value;
                      const selectedSprintData = sprintData.find(
                        (sprint) => sprint.sprint_name === selectedSprintName
                      );
                      setSelectedSprint({
                        name: selectedSprintName,
                        startDate: selectedSprintData ? selectedSprintData.sprint_start_date : "",
                        endDate: selectedSprintData ? selectedSprintData.sprint_end_date : "",
                      });
                    }}
                  >
                    <option value="">{intl.formatMessage({ id: "selectSprint" })}</option>
                    {sprintData.map((sprint, index) => (
                      <option key={index} value={sprint.sprint_name}>{sprint.sprint_name}</option>
                    ))}
                  </select>
                </Box>

                <Box sx={{ width: '300px' }}>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', mb: 1, display: 'block' }}>
                    {intl.formatMessage({ id: "talentSelection" })}
                  </Typography>
                  <Select
                    isMulti
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    options={Array.isArray(candidates) ? candidates.map(candidate => ({
                      value: candidate.Candidate_Name,
                      label: candidate.Candidate_Name
                    })) : []}
                    value={selectedCandidate}
                    onChange={(selectedOptions) => setSelectedCandidate(selectedOptions || [])}
                    placeholder="Select Candidates"
                    className="artemy-select-container"
                    classNamePrefix="artemy-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        background: 'var(--artemy-bg-elevated)',
                        borderColor: 'var(--artemy-border)',
                        '&:hover': { borderColor: 'var(--artemy-primary)' }
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        background: 'var(--artemy-bg-elevated)',
                        border: '1px solid var(--artemy-border)',
                        zIndex: 9999
                      }),
                      option: (base, state) => ({
                        ...base,
                        background: state.isFocused ? 'rgba(255,255,255,0.05)' : 'transparent',
                        color: 'var(--artemy-text-primary)'
                      }),
                      multiValue: (base) => ({
                        ...base,
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '4px'
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: 'var(--artemy-text-primary)'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: 'var(--artemy-text-secondary)'
                      })
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Kanban Board */}
        <Box sx={{ position: 'relative' }}>
          {selectedPodId?.length > 0 && !selectedSprint.name && (
            <Alert severity="warning" sx={{ mb: 3, mx: 'auto', maxWidth: '800px', borderRadius: 2 }}>
              Please select a sprint from the dropdown above to view the tasks for the selected pod.
            </Alert>
          )}
          <button
            className="artemy-btn-circle"
            style={{ position: 'absolute', left: '-20px', top: '50%', zIndex: 10 }}
            onClick={() => handleScroll("left")}
          >
            ←
          </button>

          <DragDropContext onDragEnd={onDragEnd}>
            <Box
              ref={scrollContainerRef}
              sx={{
                display: 'flex',
                gap: 3,
                overflowX: 'auto',
                pb: 4,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {Object.entries(columns).map(([columnId, column]) => (
                <Droppable key={columnId} droppableId={columnId}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="artemy-card"
                      sx={{
                        minWidth: '320px',
                        width: '320px',
                        maxHeight: 'calc(100vh - 350px)',
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: 'rgba(255,255,255,0.02)',
                        borderColor: 'rgba(255,255,255,0.05)'
                      }}
                    >
                      <Box sx={{
                        p: 2,
                        borderBottom: `2px solid ${column.type === "closed" ? 'var(--artemy-green)' : 'var(--artemy-primary)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--artemy-text-primary)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                          {column.title}
                        </Typography>
                        <Chip
                          label={column.items.length}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.1)',
                            color: 'var(--artemy-text-secondary)',
                            fontSize: '0.7rem'
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 2 }}>
                        {loading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress size={24} sx={{ color: 'var(--artemy-primary)' }} />
                          </Box>
                        ) : column.items.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 8, opacity: 0.3 }}>
                            <Typography variant="caption">No items</Typography>
                          </Box>
                        ) : (
                          column.items.map((task, index) => {
                            const taskId = task.key || task.pod_task_id;
                            const isSelected = selectedTasks.includes(taskId);

                            return (
                              <Draggable
                                key={taskId}
                                draggableId={taskId.toString()}
                                index={index}
                              >
                                {(provided) => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="artemy-card artemy-card-hover"
                                    onClick={() => handleTaskClick(task)}
                                    sx={{
                                      mb: 2,
                                      p: 2,
                                      borderLeft: `4px solid ${column.type === "closed" ? 'var(--artemy-green)' : 'var(--artemy-primary)'}`,
                                      bgcolor: isSelected ? 'rgba(var(--artemy-primary-rgb), 0.1)' : 'var(--artemy-bg-elevated)',
                                      position: 'relative'
                                    }}
                                  >
                                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                                      <Checkbox
                                        size="small"
                                        checked={isSelected}
                                        onChange={(e) => handleTaskSelection(taskId, e)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ color: 'var(--artemy-border)', '&.Mui-checked': { color: 'var(--artemy-primary)' } }}
                                      />
                                    </Box>

                                    <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', mb: 1, display: 'block' }}>
                                      {taskId}
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 1, mb: 1, pr: 3 }}>
                                      {task.key ? (
                                        <FaJira size={14} color="var(--artemy-primary)" style={{ flexShrink: 0, marginTop: 4 }} />
                                      ) : (
                                        <FaBug size={14} color="var(--artemy-green)" style={{ flexShrink: 0, marginTop: 4 }} />
                                      )}
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--artemy-text-primary)' }}>
                                        {task.summary || task.pod_task_name}
                                      </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon sx={{ fontSize: 14, color: 'var(--artemy-text-secondary)' }} />
                                        <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>
                                          {task.Candidate_Name || 'Unassigned'}
                                        </Typography>
                                      </Box>
                                      {task.assigned_agent && (
                                        <Tooltip title={`Agent: ${task.assigned_agent.name}`}>
                                          <SmartToyIcon sx={{ fontSize: 16, color: 'var(--artemy-primary)' }} />
                                        </Tooltip>
                                      )}
                                    </Box>

                                    <Typography variant="caption" sx={{ mt: 1, color: 'var(--artemy-text-secondary)', display: 'block', fontSize: '0.7rem' }}>
                                      {intl.formatMessage({ id: "dueDate" })}: {moment(task.due_date).format("MM-DD-YYYY") || "Not Set"}
                                    </Typography>
                                  </Box>
                                )}
                              </Draggable>
                            );
                          })
                        )}
                        {provided.placeholder}
                      </Box>
                    </Box>
                  )}
                </Droppable>
              ))}
            </Box>
          </DragDropContext>

          <button
            className="artemy-btn-circle"
            style={{ position: 'absolute', right: '-20px', top: '50%', zIndex: 10 }}
            onClick={() => handleScroll("right")}
          >
            →
          </button>
        </Box>

        {/* Action Bar - Unified Flow */}
        <Slide direction="up" in={selectedTasks.length > 0} mountOnEnter unmountOnExit>
          <Box sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'auto',
            minWidth: '600px',
            bgcolor: 'var(--artemy-bg-elevated)',
            border: '1px solid var(--artemy-primary)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 1100,
            backdropFilter: 'blur(20px)'
          }}>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--artemy-text-primary)' }}>
                {selectedTasks.length} {selectedTasks.length === 1 ? 'task' : 'tasks'} selected
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>
                Ready to assign agents and generate code
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mr: 1 }}>
              <button
                className="artemy-btn"
                style={{ background: 'transparent', border: '1px solid var(--artemy-border)' }}
                onClick={handleClearSelection}
              >
                Clear
              </button>
              <button
                className="artemy-btn"
                onClick={handleCompleteFlow}
                disabled={agents.length === 0}
              >
                Start Workflow
              </button>
            </Box>
          </Box>
        </Slide>

        {/* Agent Selection Dialog */}
        <Dialog
          open={showAgentSelectionDialog}
          onClose={handleCloseAgentDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            className: "artemy-card",
            sx: { bgcolor: 'var(--artemy-bg-elevated)', backgroundImage: 'none', border: '1px solid var(--artemy-border)' }
          }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid var(--artemy-border)', pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SmartToyIcon sx={{ color: 'var(--artemy-primary)' }} />
              <Typography variant="h6" className="artemy-main" sx={{ fontWeight: 600 }}>Select AI Agent</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ color: 'var(--artemy-text-secondary)', mb: 4 }}>
              Assign an AI agent to {selectedTasks.length} {selectedTasks.length === 1 ? 'task' : 'tasks'}.
              The agent will handle code generation and submission.
            </Typography>

            <Select
              options={agents.map(agent => ({
                value: agent.id,
                label: `${agent.name} (${agent.agent_type})`,
                agent: agent
              }))}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              value={selectedAgentForBulk ? agents.map(agent => ({
                value: agent.id,
                label: `${agent.name} (${agent.agent_type})`,
                agent: agent
              })).find(opt => opt.value === selectedAgentForBulk) : null}
              onChange={(option) => setSelectedAgentForBulk(option?.value || null)}
              placeholder="Select an agent..."
              className="artemy-select-container"
              classNamePrefix="artemy-select"
              styles={{
                control: (base) => ({
                  ...base,
                  background: 'rgba(255,255,255,0.05)',
                  borderColor: 'var(--artemy-border)',
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({
                  ...base,
                  background: 'var(--artemy-bg-elevated)',
                  border: '1px solid var(--artemy-border)',
                  zIndex: 9999
                }),
                option: (base, state) => ({
                  ...base,
                  background: state.isFocused ? 'rgba(255,255,255,0.1)' : 'transparent',
                  color: 'var(--artemy-text-primary)'
                }),
                singleValue: (base) => ({
                  ...base,
                  color: 'var(--artemy-text-primary)'
                }),
                placeholder: (base) => ({
                  ...base,
                  color: 'var(--artemy-text-secondary)'
                }),
                input: (base) => ({
                  ...base,
                  color: 'var(--artemy-text-primary)'
                })
              }}
              formatOptionLabel={(option) => {
                if (!option.agent) return option.label;
                return (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.agent.name}</Typography>
                      <Chip
                        label={option.agent.status}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'var(--artemy-text-secondary)', fontSize: '0.65rem' }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>{option.agent.agent_type}</Typography>
                  </Box>
                );
              }}
            />

            {agents.length === 0 && (
              <Box className="artemy-card" sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,165,0,0.1)', border: '1px solid orange' }}>
                <Typography variant="caption" sx={{ color: 'orange' }}>No agents available.</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid var(--artemy-border)' }}>
            <button className="artemy-btn" style={{ background: 'transparent' }} onClick={handleCloseAgentDialog}>Cancel</button>
            <button className="artemy-btn" onClick={handleStartWorkflow} disabled={!selectedAgentForBulk}>Start Workflow</button>
          </DialogActions>
        </Dialog>

        {/* Progress Dialog */}
        <Dialog
          open={isGeneratingCode}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            className: "artemy-card",
            sx: { bgcolor: 'var(--artemy-bg-elevated)', backgroundImage: 'none' }
          }}
        >
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <CircularProgress size={60} sx={{ mb: 4, color: 'var(--artemy-primary)' }} />
              <Typography variant="h6" className="artemy-main" sx={{ mb: 2 }}>{progressMessage}</Typography>
              <Box sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '10px', height: '8px', overflow: 'hidden', mb: 2 }}>
                <Box sx={{ width: `${currentProgress}%`, height: '100%', bgcolor: 'var(--artemy-primary)', transition: 'width 0.3s ease' }} />
              </Box>
              <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>
                {Math.round(currentProgress)}% Complete
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Task Details Drawer */}
        <Drawer
          anchor="right"
          open={!!selectedTask}
          onClose={handleDrawerClose}
          PaperProps={{
            sx: { width: 600, bgcolor: 'var(--artemy-bg-elevated)', backgroundImage: 'none', borderLeft: '1px solid var(--artemy-border)', color: 'var(--artemy-text-primary)' }
          }}
        >
          <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'var(--artemy-primary)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                  Task Assignment
                </Typography>
              </Box>
              <IconButton onClick={handleDrawerClose} sx={{ color: 'var(--artemy-text-secondary)' }}><CloseIcon /></IconButton>
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {selectedTask?.key || selectedTask?.pod_task_id}
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--artemy-text-secondary)', mb: 4 }}>
              {selectedTask?.summary || selectedTask?.pod_task_name}
            </Typography>

            <Box className="artemy-card" sx={{ p: 3, mb: 4 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', display: 'block', mb: 0.5 }}>Assignee</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon size={14} sx={{ color: 'var(--artemy-green)' }} />
                    <Typography variant="body2">{selectedTask?.Candidate_Name || 'Not Assigned'}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', display: 'block', mb: 0.5 }}>Due Date</Typography>
                  <Typography variant="body2">{moment(selectedTask?.due_date).format("MMM DD, YYYY") || "Not Set"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', display: 'block', mb: 0.5 }}>Status</Typography>
                  <Chip label={selectedTask?.status} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: 'var(--artemy-text-primary)' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)', display: 'block', mb: 0.5 }}>Priority</Typography>
                  <Typography variant="body2">{selectedTask?.priority || 'Normal'}</Typography>
                </Box>
              </Box>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>AI Agent Workflow</Typography>
            <Box className="artemy-card" sx={{ p: 3, mb: 4, bgcolor: 'rgba(var(--artemy-primary-rgb), 0.05)', borderColor: 'rgba(var(--artemy-primary-rgb), 0.2)' }}>
              {selectedTask?.assigned_agent ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <SmartToyIcon sx={{ color: 'var(--artemy-primary)' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedTask.assigned_agent.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>{selectedTask.assigned_agent.agent_type}</Typography>
                      </Box>
                    </Box>
                    <Chip label={selectedTask.assigned_agent.status} size="small" color={getAgentStatusColor(selectedTask.assigned_agent.status)} />
                  </Box>
                  <button className="artemy-btn" style={{ width: '100%', background: 'rgba(255,0,0,0.1)', border: '1px solid rgba(255,0,0,0.3)', color: '#ff4d4d' }} onClick={handleRemoveAgent}>
                    Remove Agent
                  </button>
                </Box>
              ) : (
                <Box>
                  <Select
                    options={agents.map(agent => ({
                      value: agent.id,
                      label: `${agent.name} (${agent.agent_type})`,
                      agent: agent
                    }))}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    value={selectedAgent}
                    onChange={setSelectedAgent}
                    placeholder="Select Agent..."
                    className="artemy-select-container"
                    classNamePrefix="artemy-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        background: 'rgba(255,255,255,0.05)',
                        borderColor: 'var(--artemy-border)',
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        background: 'var(--artemy-bg-elevated)',
                        border: '1px solid var(--artemy-border)',
                        zIndex: 9999
                      }),
                      option: (base, state) => ({
                        ...base,
                        background: state.isFocused ? 'rgba(255,255,255,0.1)' : 'transparent',
                        color: 'var(--artemy-text-primary)'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: 'var(--artemy-text-primary)'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: 'var(--artemy-text-secondary)'
                      }),
                      input: (base) => ({
                        ...base,
                        color: 'var(--artemy-text-primary)'
                      })
                    }}
                  />
                  <button
                    className="artemy-btn"
                    style={{ width: '100%', marginTop: '16px' }}
                    onClick={() => selectedAgent && handleAssignAgent(selectedAgent.value)}
                    disabled={!selectedAgent || isAssigningAgent}
                  >
                    {isAssigningAgent ? 'Assigning...' : 'Assign & Start Work'}
                  </button>
                </Box>
              )}
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Discussion</Typography>
            <Box sx={{ flex: 1, overflowY: 'auto', mb: 3 }}>
              {renderComments()}
            </Box>

            <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid var(--artemy-border)' }}>
              <textarea
                className="artemy-input"
                style={{ width: '100%', height: '80px', marginBottom: '16px', background: 'rgba(255,255,255,0.03)' }}
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <button
                className="artemy-btn"
                style={{ float: 'right' }}
                onClick={handleAddComment}
                disabled={!newComment.trim()}
              >
                Post Comment
              </button>
            </Box>
          </Box>
        </Drawer>

        {/* Global Notification */}
        <Snackbar
          open={showNotification}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <div className="artemy-card" style={{
            padding: '12px 24px',
            background: notificationMessage.includes('✅') ? 'var(--artemy-green)' : 'var(--artemy-primary)',
            color: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <Typography variant="body2">{notificationMessage}</Typography>
          </div>
        </Snackbar>
      </Box>
    </div>
  );
};

export default KanbanBoard;